import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { Accordion, AsyncDataHandler, CheckboxSelections } from '@ds';
import { AccordionSize, AccordionVariant } from '@ds/elements/Accordion/Accordion.types';
import { useGetBeneficiary } from 'src/shared/queries/useGetBeneficiary';
import { useGetUser } from 'src/shared/queries/useGetUser';
import { useGetDependents } from 'src/shared/queries/useGetDependents';
import { IBeneficiary, IDependent } from 'src/shared/types/Beneficiary';
import UserIcon from 'src/components/UserIcon/UserIcon';
import { UserIconType } from 'src/utils/constants/User';
import { DependentRelationship } from 'src/shared/types/Common';
import { useGetImmigrationStatusInfo } from 'src/shared/queries/useGetImmigrationStatusInfo';
import TabContent from './TabContent';
import PermanentResidentDetails from './PermanentResident/PermanentResidentDetails';
import { useNavigateWithSecureParams } from 'src/utils/hooks/useNavigateWithSecureParams';
import RemoteDocsBanner from 'src/components/RemoteDocsBanner/RemoteDocsBanner';
import { GlobalCaseTransitionModalType } from 'src/components/GlobalCaseTransitionModal/GlobalCaseTransitionModal';
import styles from './ImmigrationStatus.module.scss';

const ImmigrationStatus: React.FC = () => {
  const { getParam } = useNavigateWithSecureParams();
  const urlSearchParamId = getParam('id');
  const { data: userInfo } = useGetUser();
  const beneId = Number(userInfo?.beneId ? Number(userInfo.beneId) : undefined);
  const { data: beneficiary, isLoading: isBeneficiaryLoading, error: beneficiaryError } = useGetBeneficiary(beneId);
  const { data: dependents, isLoading: isDependentsLoading } = useGetDependents(beneId);
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const [selectedDependentId, setSelectedDependentId] = useState<number | undefined>(undefined);
  const { data: immigrationStatuses } = useGetImmigrationStatusInfo(
    selectedDependentId ? undefined : beneId,
    selectedDependentId
  );
  const [showActiveOnly, setShowActiveOnly] = useState(false);

  useEffect(() => {
    if (!urlSearchParamId || !beneficiary || !dependents) return;

    if (urlSearchParamId == String(beneficiary.beneId)) {
      setActiveTabIndex(0);
      return;
    }

    const dependentIndex = dependents.findIndex((dependent) => urlSearchParamId == String(dependent.dependentId));

    if (dependentIndex !== -1) {
      setActiveTabIndex(dependentIndex + 1);
    }
  }, [urlSearchParamId, beneficiary, dependents]);

  useEffect(() => {
    const newDependentId = activeTabIndex > 0 ? dependents?.[activeTabIndex - 1]?.dependentId : undefined;
    setSelectedDependentId(newDependentId);
  }, [activeTabIndex, dependents]);

  const getTabs = (beneficiary: IBeneficiary, dependents: IDependent[]) => {
    const tabsData = [
      beneficiary
        ? {
            name: `${beneficiary.fullName} (Principal)`,
            isActive: activeTabIndex === 0,
            iconType: UserIconType.SINGLE,
          }
        : null,
      ...(dependents?.map((dependent, index) => ({
        name: dependent.fullName,
        isActive: activeTabIndex === index + 1,
        iconType: dependent.relationship === DependentRelationship.Spouse ? UserIconType.TWO : UserIconType.MULTI,
      })) || []),
    ].filter(Boolean);

    return tabsData.map((tab, index) => {
      const isBeneficiary = index === 0;
      const dob = isBeneficiary ? beneficiary.dob : dependents[index - 1].dob;
      const cob = isBeneficiary ? beneficiary.cob : dependents[index - 1].cob;

      return {
        header: tab ? (
          <div
            className={classNames(styles['tab-header'], {
              [styles['tab-header__active']]: tab.isActive,
            })}
          >
            <UserIcon className={styles['user-icon']} userIcon={tab.iconType} />
            <h5>{tab.name}</h5>
          </div>
        ) : null,
        body: (
          <TabContent
            dob={dob}
            cob={cob}
            isBeneficiary={isBeneficiary}
            statusInfo={immigrationStatuses?.[0]}
            beneId={beneId}
            dependentId={selectedDependentId}
            showActiveOnly={showActiveOnly}
          />
        ),
      };
    });
  };

  return (
    <div className={`${styles['immigration-status-page']}`}>
      <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
        <h2>Immigration Status</h2>
        <CheckboxSelections
          className={`m-2 ${styles['active-only-checkbox']}`}
          options={[{ label: 'Active Only', checked: showActiveOnly }]}
          onCheckboxChanged={() => setShowActiveOnly((prev) => !prev)}
        />
      </div>

      <RemoteDocsBanner
        pathname="immigration-status"
        classNames="mt-4 -mb-4"
        type={GlobalCaseTransitionModalType.IMMIGRATION_DOCUMENTS}
      />

      <div className={`${styles['immigration-status-page__content']}`}>
        <div className={styles['users-immigration-status-section']}>
          {
            <AsyncDataHandler isLoading={isBeneficiaryLoading || isDependentsLoading} error={beneficiaryError}>
              {beneficiary != null && dependents != null && (
                <Accordion
                  className={styles['users-immigration-status-accordion']}
                  activeIndex={activeTabIndex}
                  tabs={getTabs(beneficiary, dependents)}
                  variant={AccordionVariant.Secondary}
                  size={AccordionSize.Large}
                  onTabChange={(event) => setActiveTabIndex(event.index as number)}
                />
              )}
            </AsyncDataHandler>
          }
        </div>
        <PermanentResidentDetails />
      </div>
    </div>
  );
};

export default ImmigrationStatus;
